import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { DEFAULT_WALLET_NETWORK, getWalletNetwork } from "@/lib/networks";

export interface LiveBalance {
  id: string;
  crypto_symbol: string;
  crypto_name: string;
  balance: number;
  network: string;
}

/**
 * Live balance tracker for a single wallet network:
 *  - Polls the DB every second silently (no loading flicker).
 *  - Subscribes to Postgres realtime changes on `wallets` for instant updates.
 *  - Guards against transient 0-drops: only accepts a lower balance after
 *    it has been observed for `stableDrops` consecutive polls, so brief RPC
 *    glitches never blank the UI.
 */
export function useLiveBalances(
  userId: string | undefined,
  opts?: { intervalMs?: number; stableDrops?: number; network?: string },
) {
  const intervalMs = opts?.intervalMs ?? 1000;
  const stableDrops = opts?.stableDrops ?? 3;
  const network = opts?.network ?? DEFAULT_WALLET_NETWORK;
  const [balances, setBalances] = useState<LiveBalance[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const lastGoodRef = useRef<Record<string, number>>({});
  const dropCountRef = useRef<Record<string, number>>({});

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    lastGoodRef.current = {};
    dropCountRef.current = {};
    setBalances([]);
    const supported = new Set(getWalletNetwork(network).symbols);

    const applyIncoming = (rows: LiveBalance[]) => {
      const normalized = rows
        .map(r => ({ ...r, crypto_symbol: (r.crypto_symbol || "").toUpperCase() }))
        .filter(r => supported.has(r.crypto_symbol));

      const merged: Record<string, LiveBalance> = {};
      for (const r of normalized) {
        const sym = r.crypto_symbol;
        const incoming = Number(r.balance || 0);
        const last = lastGoodRef.current[sym] ?? 0;
        let accepted = incoming;
        // If new value drops significantly below last-known-good, delay accepting it
        if (incoming < last * 0.5 && last > 0) {
          dropCountRef.current[sym] = (dropCountRef.current[sym] || 0) + 1;
          if (dropCountRef.current[sym] < stableDrops) {
            accepted = last;
          } else {
            lastGoodRef.current[sym] = incoming;
            dropCountRef.current[sym] = 0;
          }
        } else {
          lastGoodRef.current[sym] = incoming;
          dropCountRef.current[sym] = 0;
        }
        merged[sym] = { ...r, balance: accepted };
      }
      if (!cancelled) setBalances(Object.values(merged).sort((a, b) => b.balance - a.balance));
    };

    const poll = async () => {
      const { data, error } = await supabase
        .from("wallets")
        .select("id, crypto_symbol, crypto_name, balance, network")
        .eq("user_id", userId)
        .eq("network", network);
      if (!error && data) applyIncoming(data as LiveBalance[]);
      if (!cancelled) setInitialLoading(false);
    };

    poll();
    const timer = setInterval(poll, intervalMs);

    const channel = supabase
      .channel(`wallets-live-${userId}-${network}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "wallets", filter: `user_id=eq.${userId}` }, () => {
        poll();
      })
      .subscribe();

    return () => {
      cancelled = true;
      clearInterval(timer);
      supabase.removeChannel(channel);
    };
  }, [userId, intervalMs, stableDrops, network]);

  return { balances, initialLoading };
}
